/**
 * Panel state: the API snapshot patched by live events, the connection health, the undo
 * window of every approval and the team stop state. React reads it through
 * useSyncExternalStore; every update replaces the state object.
 *
 * Safety rules enforced here, not only in the buttons:
 * - no approval while the data is stale, the team is stopped or paused, or the Denetçi has
 *   not passed the proposal;
 * - every T3-other proposal (T4) is approved only after the completed hold, with the hold
 *   token the server issued when the hold started, sent no earlier than its notBefore;
 * - every executor action waits for its undo window; undo returns the proposal to the
 *   queue under the new id the server gives it (never the old object);
 * - the kill switch undoes held approvals on the server; the panel drops them and reloads;
 * - a refused decision says why (the contract's error code) and reloads the snapshot when
 *   the screen was out of date.
 */

import {
  type ConnectionSlot,
  type ConnectionsResponse,
  type ConnectionTest,
  connectionValueAccepted,
  ERROR_MESSAGES_TR,
} from '@twin/contracts';
import {
  AlreadyAppliedError,
  decisionErrorText,
  needsSnapshotRefresh,
  PolicyRejectedError,
  TransportError,
  type TwinApi,
  TwinApiError,
} from '../api/client.js';
import { type LiveEvent, type LiveEvents, STALE_AFTER_MS } from '../api/live.js';
import { applyChange, confirmationViolation, evaluateChange, reasonText } from '../api/policy-rules.js';
import type {
  AuditEntry,
  PolicyChange,
  PreferencesPatch,
  Connection as ServiceConnection,
  Settings,
} from '../api/settings.js';
import {
  type Agent,
  type AgentId,
  type ApprovalTicket,
  type DoneItem,
  type HoldTicket,
  type Instant,
  type PanelSystem,
  type Proposal,
  REQUEST_REJECT_REASON_LABELS,
  type RejectedItem,
  type RejectReasonCode,
  type RequestRejectCode,
  type RequestStatus,
  type Snapshot,
} from '../api/types.js';
import {
  acceptedRequestNote,
  deferredRequestNote,
  formatClock,
  formatIsoDay,
  setCurrencyDisplay,
} from '../lib/format.js';
import { ApiUsageStore } from './api-usage.js';
import { ChannelsStore } from './channels.js';
import { CustomersStore } from './customers.js';
import { EvalStore } from './evals.js';
import { KbStore } from './kb.js';
import { LeadsStore } from './leads.js';
import { LogStore } from './log.js';
import { McpAddressStore } from './mcp-addresses.js';
import { OrganizationStore } from './organization.js';
import { OutreachStore } from './outreach.js';
import { OutreachLogStore } from './outreach-log.js';
import { OzetStore } from './ozet.js';
import { PeopleStore } from './people.js';
import { ProductsStore } from './products.js';
import { ProspectsStore } from './prospects.js';
import { ReplyExamplesStore } from './reply-examples.js';
import { SalesMapStore } from './sales-map.js';
import { SekreterStore } from './sekreter.js';
import { outsidePolicy, requiresHold } from './selectors.js';
import { SeoReportStore } from './seo-report.js';
import { ShopStore } from './shop.js';
import { SiteChatsStore } from './site-chats.js';
import { SiteStatsStore } from './site-stats.js';
import { VoiceStore } from './voice.js';

export type Connection = 'connecting' | 'live' | 'stale';

/** Bağlantıyı dene: how often the panel re-reads the connections while a test runs, and for how long. */
export const CONNECTION_TEST_POLL_MS = 2_000;
export const CONNECTION_TEST_POLL_LIMIT_MS = 30_000;
export type TeamMode = 'running' | 'paused' | 'stopped';

export interface PendingApproval {
  /** approval_id once the server recorded the decision; null while the request is in flight. */
  ticketId: string | null;
  proposal: Proposal;
  /** Queue position to restore when the server refuses the decision. */
  index: number;
  applyAt: Instant | null;
}

export interface AwaitingManual {
  proposalId: string;
  title: string;
  /** A copied customer reply (Etsy); otherwise another manual step such as a refund. */
  copy: boolean;
}

export interface Toast {
  id: string;
  tone: 'ink' | 'danger';
  icon: 'check' | 'pause' | 'alert' | 'info';
  message: string;
  /** Countdown bar for undo windows, in local milliseconds. */
  countdown: { startedAt: number; durationMs: number } | null;
  /** keepOpen: the action closes the toast itself when it succeeds (undo can fail and be retried). */
  action: { label: string; run: () => void; keepOpen?: boolean } | null;
  link: { label: string; href: string } | null;
}

export interface PanelState {
  snapshot: Snapshot | null;
  loadError: string | null;
  connection: Connection;
  /** Server time of the last heartbeat, shown as "07:41" or "07:12 verisi". */
  lastHeartbeatAt: Instant | null;
  now: Instant;
  pending: PendingApproval[];
  toasts: Toast[];
  /** From the snapshot's kill switch (system event), or the local change until the server confirms. */
  team: { mode: TeamMode; since: Instant | null };
  /** Approvals whose last step is manual (copied replies, refunds): Sercan marks them done. */
  awaitingSend: AwaitingManual[];
  /** Ask "Cevabı Etsy'de gönderdin mi?" when he comes back to the app. */
  askSent: boolean;
  /** Short input lock after auto-advance, local milliseconds. */
  inputLockUntil: number;
  /** Rejected proposals: the server's last 7 days plus this session's, newest first. */
  rejected: RejectedItem[];
  /** Ayarlar read model: connections, policy, hard limits, preferences. */
  settings: Settings | null;
  settingsError: string | null;
  /** Bağlantı değerleri (0.4.1): status and last4 per slot, never a value. */
  connections: ConnectionsResponse | null;
  connectionsError: string | null;
  /** Slots with a request in flight (save, clear, test): their controls are disabled. */
  connectionBusy: readonly ConnectionSlot[];
}

/** Options of a policy change. Loosening needs both; tightening needs neither. */
export interface PolicyChangeOptions {
  reason?: string | null;
  /** Set only by the completed 1.5 s hold. */
  confirmedByHold?: boolean;
}

/** The change that puts a setting back to its value in the given settings. */
function restoringChange(settings: Settings, change: PolicyChange): PolicyChange | null {
  switch (change.kind) {
    case 'number': {
      const policy = settings.policies.find((item) => item.id === change.id);
      return policy ? { ...change, value: policy.value } : null;
    }
    case 'stage':
      return { ...change, value: settings.stage.value };
    case 'flag': {
      const flag = settings.flags.find((item) => item.id === change.id);
      return flag ? { ...change, enabled: flag.enabled } : null;
    }
    case 'agent': {
      const agent = settings.agents.find((item) => item.id === change.id);
      return agent ? { ...change, enabled: agent.enabled } : null;
    }
  }
}

const TOKEN_HOLDER_DATIVE = { executor: 'dağıtıcıya', ingest: 'içe alma işçisine' } as const;

export type ApproveBlocker =
  | 'offline'
  | 'stopped'
  | 'paused'
  | 'locked'
  | 'missing'
  | 'expired'
  | 'verifier'
  | 'policy'
  | null;

export interface ApproveOptions {
  /** Düzelt: the edited outgoing text. Never allowed on items that need the hold. */
  editedText?: string | null;
  /** Set only by the completed 1.5 s hold. Items that need the hold are refused without it. */
  confirmedByHold?: boolean;
  /**
   * Onayla ve kopyala: true only when the outgoing text really reached the clipboard. A manual
   * handoff is not recorded without it, so the panel never says "Kopyalandı" when it was not.
   */
  copied?: boolean;
}

export interface PanelStoreOptions {
  api: TwinApi;
  live: LiveEvents;
  clock: { now(): Instant };
  /** Local clock for staleness and countdowns (Date.now by default). */
  localNow?: () => number;
  /**
   * Local monotonic clock of hold tickets (HoldTicket.sendNotBeforeLocal). Must be the same
   * clock the API client uses; the app passes performance.now to both, tests the default.
   */
  monoNow?: () => number;
  tickMs?: number;
}

/** 2.5 step 9: the server wants a hold token for every tier other than T3. */
function needsHoldToken(proposal: Proposal): boolean {
  return proposal.tier !== 'T3';
}

const INFO_TOAST_MS = 4_000;
const APPLIED_TOAST_MS = 3_000;
export const INPUT_LOCK_MS = 400;

/** Team mode of the kill switch level. */
export function teamFromSystem(system: PanelSystem): PanelState['team'] {
  const level = system.killSwitch.level;
  if (level === 'none') return { mode: 'running', since: null };
  return { mode: level === 'pause' ? 'paused' : 'stopped', since: system.killSwitch.since };
}

/** Server and session rejections together, one row per proposal, newest first. */
function mergeRejected(...lists: readonly RejectedItem[][]): RejectedItem[] {
  const byId = new Map<string, RejectedItem>();
  for (const item of lists.flat()) {
    const known = byId.get(item.proposalId);
    if (!known || known.at < item.at) byId.set(item.proposalId, item);
  }
  return [...byId.values()].sort((a, b) => b.at - a.at);
}

interface HoldRequest {
  payloadHash: string;
  ticket: Promise<HoldTicket>;
}

export class PanelStore {
  private state: PanelState;
  private readonly listeners = new Set<() => void>();
  private readonly api: TwinApi;
  private readonly live: LiveEvents;
  private readonly clock: { now(): Instant };
  private readonly localNow: () => number;
  private readonly monoNow: () => number;
  private readonly tickMs: number;
  private lastHeartbeatLocal: number | null = null;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private toastSeq = 0;
  private stopLive: (() => void) | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;
  /** Undo toast per held ticket, so undo, apply or the kill switch can close it. */
  private readonly undoToasts = new Map<string, string>();
  /** Hold tokens asked when a T4 hold started, per proposal; dropped when the hold is released. */
  private readonly holds = new Map<string, HoldRequest>();
  /** Entity events that arrive before the first snapshot; replayed once it is loaded. */
  private buffered: LiveEvent[] = [];
  private refreshing: Promise<void> | null = null;
  /** A reload was asked while one was in flight: its answer may predate the reason, so load again. */
  private refreshAgain = false;
  /** Events applied while a snapshot request was in flight; replayed over its (older) answer. */
  private duringLoad: LiveEvent[] | null = null;
  /** Hold token asked when the hold on Devam et (after a stop) started. */
  private resumeHold: Promise<HoldTicket> | null = null;
  /**
   * Bumped whenever the view may have missed events (the stream dropped, snapshot.invalidate).
   * The last event id (X-Twin-View) then already speaks for the new stream while the screen
   * still shows the old snapshot, so decisions wait for a snapshot load that started after it.
   */
  private viewGeneration = 0;
  private loadedGeneration = 0;
  /** Leadler (Harita, Huni, Öğrenme): loaded when the screen opens. */
  readonly leads: LeadsStore;
  /** Mağaza (Satışlar, İlanlar): loaded when one of the screens opens. */
  readonly shop: ShopStore;
  /** Ürünler: loaded when the screen opens. */
  readonly products: ProductsStore;
  /** Potansiyel müşteriler: live while the page is open (read every 20 s). */
  readonly prospects: ProspectsStore;
  /** Müşteriler: loaded when the screen opens. */
  readonly customers: CustomersStore;
  /** Kişi: one load per opened person. */
  readonly people: PeopleStore;
  readonly organization: OrganizationStore;
  /** Ayarlar › API kullanımı: loaded when the screen opens, again on Yenile. */
  readonly apiUsage: ApiUsageStore;
  /** Site (v0.1.166): loaded when the screen opens, again on Yenile. */
  readonly siteStats: SiteStatsStore;
  readonly channels: ChannelsStore;
  /** Satış haritası (v0.1.269): loaded when the screen opens, per period and family. */
  readonly salesMap: SalesMapStore;
  readonly voice: VoiceStore;
  readonly siteChats: SiteChatsStore;
  readonly seoReport: SeoReportStore;
  readonly evals: EvalStore;
  readonly mcpAddresses: McpAddressStore;
  readonly ozet: OzetStore;
  /** Kayıtlar: loaded when the screen opens; new events come by button. */
  readonly log: LogStore;
  readonly kb: KbStore;
  readonly replyExamples: ReplyExamplesStore;
  readonly sekreter: SekreterStore;
  readonly outreach: OutreachStore;
  /** Tanıtım takibi (v0.1.169): loaded when the screen opens, again on Yenile. */
  readonly outreachLog: OutreachLogStore;

  constructor(options: PanelStoreOptions) {
    this.api = options.api;
    this.leads = new LeadsStore(options.api, {
      canLoosen: () => this.canLoosen(),
      showToast: (message, toastOptions) => this.showToast(message, toastOptions),
      now: () => this.clock.now(),
    });
    this.shop = new ShopStore(options.api);
    this.products = new ProductsStore(options.api);
    this.prospects = new ProspectsStore(options.api, () => this.clock.now());
    this.customers = new CustomersStore(options.api);
    this.people = new PeopleStore(options.api);
    this.organization = new OrganizationStore(options.api);
    this.apiUsage = new ApiUsageStore(options.api);
    this.siteStats = new SiteStatsStore(options.api);
    this.channels = new ChannelsStore(options.api);
    this.salesMap = new SalesMapStore(options.api);
    this.voice = new VoiceStore(options.api);
    this.siteChats = new SiteChatsStore(options.api);
    this.seoReport = new SeoReportStore(options.api);
    this.evals = new EvalStore(options.api);
    this.mcpAddresses = new McpAddressStore(options.api);
    this.ozet = new OzetStore(options.api);
    this.log = new LogStore(options.api);
    this.kb = new KbStore(options.api);
    this.replyExamples = new ReplyExamplesStore(options.api);
    this.sekreter = new SekreterStore(options.api);
    this.outreach = new OutreachStore(options.api);
    this.outreachLog = new OutreachLogStore(options.api);
    this.live = options.live;
    this.clock = options.clock;
    this.localNow = options.localNow ?? (() => Date.now());
    this.monoNow = options.monoNow ?? this.localNow;
    this.tickMs = options.tickMs ?? 1_000;
    this.state = {
      snapshot: null,
      loadError: null,
      connection: 'connecting',
      lastHeartbeatAt: null,
      now: this.clock.now(),
      pending: [],
      toasts: [],
      team: { mode: 'running', since: null },
      awaitingSend: [],
      askSent: false,
      inputLockUntil: 0,
      rejected: [],
      settings: null,
      settingsError: null,
      connections: null,
      connectionsError: null,
      connectionBusy: [],
    };
  }

  // ---- React binding -------------------------------------------------------

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): PanelState => this.state;

  private set(patch: Partial<PanelState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private patchSnapshot(update: (snapshot: Snapshot) => Snapshot): void {
    if (!this.state.snapshot) return;
    this.set({ snapshot: update(this.state.snapshot) });
  }

  private later(ms: number, run: () => void): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      run();
    }, ms);
    this.timers.add(timer);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => this.later(ms, resolve));
  }

  // ---- lifecycle -----------------------------------------------------------

  /** Loads the snapshot, opens the live stream and starts the clock. Returns a stop function. */
  start(): () => void {
    void this.refresh();
    void this.loadSettings();
    this.stopLive = this.live.connect(
      (event) => this.onEvent(event),
      (status) => {
        if (status === 'error') {
          this.viewGeneration += 1;
          this.set({ connection: 'stale' });
        }
        // Back after a drop: reload the snapshot so nothing missed while offline stays stale.
        else if (this.state.connection === 'stale') {
          void this.refresh();
          void this.loadSettings();
        }
      },
    );
    this.ticker = setInterval(() => this.tick(), this.tickMs);
    return () => this.stop();
  }

  stop(): void {
    this.stopLive?.();
    this.stopLive = null;
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    this.holds.clear();
  }

  /**
   * Reloads the snapshot. A call while a request is in flight does not start a second one
   * at once, but makes sure one more load follows: the answer in flight may have been read
   * before the change that asked for the reload.
   */
  refresh(): Promise<void> {
    if (this.refreshing) {
      this.refreshAgain = true;
      return this.refreshing;
    }
    const run = async (): Promise<void> => {
      // Always settle after this assignment below, even when load finishes synchronously.
      await Promise.resolve();
      try {
        do {
          this.refreshAgain = false;
          await this.load();
        } while (this.refreshAgain);
      } finally {
        // Cleared in the same step as the last refreshAgain check: a reload asked after it
        // starts a new load instead of joining one that has already finished.
        this.refreshing = null;
      }
    };
    this.refreshing = run();
    return this.refreshing;
  }

  private async load(): Promise<void> {
    this.duringLoad = [];
    const generation = this.viewGeneration;
    try {
      const snapshot = await this.api.getSnapshot();
      this.loadedGeneration = Math.max(this.loadedGeneration, generation);
      const now = this.clock.now();
      // A held approval leaves the local list once the server shows it as a Yapılanlar row
      // (applied, queued after the window, cancelled, failed, dry run): the server decides
      // what happened, the panel does not assume "applied" because a time has passed.
      const doneById = new Map(snapshot.done.map((item) => [item.id, item]));
      const pending = this.state.pending.filter((item) => {
        if (item.ticketId === null) return true;
        const done = doneById.get(item.ticketId);
        if (!done || this.stillInWindow(item, done, now)) return true;
        this.closeUndoToast(item.ticketId);
        return false;
      });
      const pendingIds = new Set(pending.map((item) => item.proposal.id));
      const settled = new Set(snapshot.done.map((item) => item.id));
      this.set({
        pending,
        snapshot: { ...snapshot, proposals: snapshot.proposals.filter((proposal) => !pendingIds.has(proposal.id)) },
        loadError: null,
        now,
        team: teamFromSystem(snapshot.system),
        rejected: mergeRejected(snapshot.rejected, this.state.rejected),
        awaitingSend: this.state.awaitingSend.filter((item) => {
          const done = doneById.get(item.proposalId);
          return !settled.has(item.proposalId) || done?.status === 'awaiting_send';
        }),
      });
      // Events are whole objects and idempotent by id (3.6): the ones that arrived before the
      // first snapshot, and the ones that arrived while this answer was on its way, are
      // applied again on top of it, so an older answer never undoes a newer event.
      const replay = [...this.buffered, ...(this.duringLoad ?? [])];
      this.buffered = [];
      this.duringLoad = null;
      for (const event of replay) this.onEvent(event);
    } catch (error) {
      this.duringLoad = null;
      this.set({ loadError: error instanceof Error ? error.message : String(error) });
    }
  }

  /** A queued row for an approval whose local undo window is still open does not end it. */
  private stillInWindow(item: PendingApproval, done: DoneItem, now: Instant): boolean {
    return done.status === 'queued' && item.applyAt !== null && now < item.applyAt;
  }

  /** Clock tick: advances "now" and switches to stale after 30 s without a heartbeat. */
  tick(): void {
    const patch: Partial<PanelState> = { now: this.clock.now() };
    if (
      this.state.connection === 'live' &&
      this.lastHeartbeatLocal !== null &&
      this.localNow() - this.lastHeartbeatLocal > STALE_AFTER_MS
    ) {
      patch.connection = 'stale';
    }
    this.set(patch);
  }

  private onEvent(event: LiveEvent): void {
    if (!this.state.snapshot && event.type !== 'heartbeat') {
      this.buffered.push(event);
      return;
    }
    if (this.duringLoad && event.type !== 'heartbeat' && event.type !== 'snapshot.invalidate') {
      this.duringLoad.push(event);
    }
    switch (event.type) {
      case 'heartbeat':
        this.lastHeartbeatLocal = this.localNow();
        this.set({ connection: 'live', lastHeartbeatAt: event.at, now: this.clock.now() });
        return;
      case 'agent':
        this.patchSnapshot((s) => ({
          ...s,
          agents: s.agents.map((agent) =>
            agent.id === event.agent.id ? this.keepLocalPause(agent, event.agent) : agent,
          ),
        }));
        return;
      case 'run':
        this.patchSnapshot((s) => ({
          ...s,
          runs: s.runs.some((run) => run.id === event.run.id)
            ? s.runs.map((run) => (run.id === event.run.id ? event.run : run))
            : [event.run, ...s.runs],
        }));
        return;
      case 'feed':
        this.patchSnapshot((s) => ({
          ...s,
          feed: [event.item, ...s.feed.filter((item) => item.id !== event.item.id)],
        }));
        return;
      case 'handoff':
        this.patchSnapshot((s) => ({
          ...s,
          handoffs: [event.handoff, ...s.handoffs.filter((item) => item.id !== event.handoff.id)],
        }));
        return;
      case 'proposal.added':
      case 'proposal.updated':
        this.upsertProposal(event.proposal);
        return;
      case 'proposal.removed':
        this.holds.delete(event.proposalId);
        this.patchSnapshot((s) => ({ ...s, proposals: s.proposals.filter((p) => p.id !== event.proposalId) }));
        return;
      case 'request.added':
      case 'request.updated':
        this.patchSnapshot((s) => ({
          ...s,
          requests: s.requests.some((request) => request.id === event.request.id)
            ? s.requests.map((request) => (request.id === event.request.id ? event.request : request))
            : [...s.requests, event.request],
        }));
        return;
      case 'request.removed':
        this.patchSnapshot((s) => ({ ...s, requests: s.requests.filter((request) => request.id !== event.requestId) }));
        return;
      case 'executor.applied':
        this.onApplied(event.ticketId, event.at);
        return;
      case 'executor.failed':
        this.onApplyFailed(event.ticketId, event.reason);
        return;
      case 'executor.dry_run':
        this.onDryRun(event.ticketId);
        return;
      case 'done':
        this.onDone(event.item);
        return;
      case 'system':
        this.patchSnapshot((s) => ({ ...s, system: event.system }));
        this.set({ team: teamFromSystem(event.system) });
        return;
      case 'snapshot.invalidate':
        this.viewGeneration += 1;
        void this.refresh();
        return;
    }
  }

  /**
   * proposal.added and proposal.updated carry the whole proposal. Idempotent by id (3.6): an
   * id already in the queue is replaced, one the panel is deciding right now is left alone.
   */
  private upsertProposal(proposal: Proposal): void {
    if (this.state.pending.some((item) => item.proposal.id === proposal.id)) return;
    this.patchSnapshot((s) => {
      const known = s.proposals.some((item) => item.id === proposal.id);
      if (!known) return { ...s, proposals: [...s.proposals, proposal] };
      // A hold begun on the old payload keeps its hash, so approve sees the change and refuses.
      return { ...s, proposals: s.proposals.map((item) => (item.id === proposal.id ? proposal : item)) };
    });
  }

  private keepLocalPause(current: Agent, incoming: Agent): Agent {
    return current.state === 'paused' ? { ...incoming, state: 'paused' } : incoming;
  }

  // ---- toasts --------------------------------------------------------------

  showToast(
    message: string,
    options: Partial<Pick<Toast, 'tone' | 'icon' | 'action' | 'link' | 'countdown'>> & { durationMs?: number } = {},
  ): string {
    this.toastSeq += 1;
    const id = `toast-${this.toastSeq}`;
    const toast: Toast = {
      id,
      tone: options.tone ?? 'ink',
      icon: options.icon ?? 'check',
      message,
      countdown: options.countdown ?? null,
      action: options.action ?? null,
      link: options.link ?? null,
    };
    // Keep at most three toasts, but never push out an open undo window: drop info toasts first.
    const toasts = [...this.state.toasts, toast];
    while (toasts.length > 3) {
      const index = toasts.findIndex((item) => item.action === null);
      if (index < 0 || index === toasts.length - 1) break;
      toasts.splice(index, 1);
    }
    this.set({ toasts });
    const duration = options.durationMs ?? toast.countdown?.durationMs ?? INFO_TOAST_MS;
    this.later(duration, () => this.dismissToast(id));
    return id;
  }

  dismissToast(id: string): void {
    if (!this.state.toasts.some((toast) => toast.id === id)) return;
    this.set({ toasts: this.state.toasts.filter((toast) => toast.id !== id) });
  }

  private closeUndoToast(ticketId: string): void {
    const toastId = this.undoToasts.get(ticketId);
    if (toastId) this.dismissToast(toastId);
    this.undoToasts.delete(ticketId);
  }

  /**
   * A refused or failed decision: the contract code's own Turkish text, or the connection hint
   * when no answer came. Codes that mean "your screen is out of date" reload the snapshot.
   */
  private decisionFailed(error: unknown, action: string, fallback: string): void {
    this.showToast(`${action} · ${decisionErrorText(error, fallback)}`, { tone: 'danger', icon: 'alert' });
    if (needsSnapshotRefresh(error)) void this.refresh();
  }

  // ---- approvals -----------------------------------------------------------

  /**
   * True when the last heartbeat is missing or older than 30 s. Checked on every decision,
   * not only by the ticker, because background tabs throttle timers: a phone coming back
   * from the pocket must not approve on data that only looks live.
   */
  private heartbeatStale(): boolean {
    return this.lastHeartbeatLocal === null || this.localNow() - this.lastHeartbeatLocal > STALE_AFTER_MS;
  }

  /**
   * Live data a decision can rest on: the stream is up, the last heartbeat is fresh and the
   * snapshot was reloaded after the last drop or snapshot.invalidate (K15).
   */
  private liveView(): boolean {
    return this.state.connection === 'live' && !this.heartbeatStale() && this.loadedGeneration >= this.viewGeneration;
  }

  /** Why approving is not possible right now, or null when it is. */
  approveBlocker(proposalId: string | null = null): ApproveBlocker {
    if (!this.liveView()) return 'offline';
    if (this.state.team.mode === 'stopped') return 'stopped';
    if (this.state.team.mode === 'paused') return 'paused';
    if (this.localNow() < this.state.inputLockUntil) return 'locked';
    if (proposalId) {
      const proposal = this.state.snapshot?.proposals.find((item) => item.id === proposalId);
      if (!proposal) return 'missing';
      // The server answers 409 expired; the panel does not offer what it cannot approve.
      if (proposal.expiresAt <= this.clock.now()) return 'expired';
      // The database refuses an approval without the Denetçi's pass; the panel says so first.
      if (proposal.verifier.verdict !== 'passed') return 'verifier';
      if (outsidePolicy(proposal, this.state.snapshot)) return 'policy';
    }
    return null;
  }

  lockInput(ms = INPUT_LOCK_MS): void {
    this.set({ inputLockUntil: this.localNow() + ms });
    this.later(ms, () => this.set({ inputLockUntil: 0 }));
  }

  /**
   * The T4 hold started (pointer down, Space, Enter or O): ask the server for the hold token
   * now, so the approval can go as soon as the hold completes. The server records the panel
   * approval view at this moment; the token is valid 1.4 s later (2.6).
   */
  beginHold(proposalId: string): void {
    const proposal = this.state.snapshot?.proposals.find((item) => item.id === proposalId);
    if (!proposal || !needsHoldToken(proposal) || this.approveBlocker(proposalId) !== null) return;
    const ticket = this.api.startHold(proposal.id, proposal.payloadHash);
    // A failed hold request is reported when the approval waits for it, not here.
    ticket.catch(() => {});
    this.holds.set(proposalId, { payloadHash: proposal.payloadHash, ticket });
  }

  /** The hold was released early or its screen went away: the token is thrown away. */
  cancelHold(proposalId: string): void {
    this.holds.delete(proposalId);
  }

  /** The hold token for this approval: the one asked at hold start, or a new one. */
  private async holdToken(proposal: Proposal): Promise<string | null> {
    const asked = this.holds.get(proposal.id);
    this.holds.delete(proposal.id);
    // The hold began on another payload: what is on screen now was not what was held for.
    if (asked && asked.payloadHash !== proposal.payloadHash) {
      this.showToast('Öneri değişti · yeniden bak', { tone: 'danger', icon: 'alert' });
      return null;
    }
    const request = asked ? asked.ticket : this.api.startHold(proposal.id, proposal.payloadHash);
    let ticket: HoldTicket;
    try {
      ticket = await request;
    } catch (error) {
      this.decisionFailed(error, 'Onay kaydedilemedi', 'bağlantıyı kontrol et');
      return null;
    }
    // Never before notBefore in server time (translated to the local clock): a slow token
    // answer must not turn into hold_too_short.
    const wait = ticket.sendNotBeforeLocal - this.monoNow();
    if (wait > 0) await this.sleep(Math.ceil(wait));
    const blocker = this.approveBlocker(proposal.id);
    if (blocker !== null && blocker !== 'locked') return null;
    return ticket.holdToken;
  }

  /**
   * Records Sercan's approval. The API holds the action for the undo window; the item leaves
   * the queue now. Returns the ticket, or null when blocked or refused.
   */
  async approve(proposalId: string, options: ApproveOptions = {}): Promise<ApprovalTicket | null> {
    if (this.approveBlocker(proposalId) !== null) return null;
    const proposal = this.state.snapshot?.proposals.find((item) => item.id === proposalId);
    if (!proposal) return null;
    const editedText = options.editedText ?? null;
    const hold = requiresHold(proposal);
    // T4 (and anything not positively T3) is approved only by the completed hold, never edited
    // and approved in one step. The server checks the hold token too (2.6).
    if (hold && (options.confirmedByHold !== true || editedText !== null)) {
      this.showToast('T4 sadece ayrıntıda 1,5 sn basılı tutarak onaylanır', { icon: 'info' });
      this.holds.delete(proposalId);
      return null;
    }
    if (
      proposal.executor.kind === 'manual_copy' &&
      proposal.executor.copyOnApprove !== false &&
      options.copied !== true
    ) {
      this.showToast('Metin kopyalanamadı · onay verilmedi, tekrar dene', { tone: 'danger', icon: 'alert' });
      return null;
    }
    // The panel holds for every item that requiresHold; the server asks the token for every
    // tier other than T3 (a T3 is approved without one, the API refuses a hold on it).
    const tokenNeeded = needsHoldToken(proposal);
    const holdToken = tokenNeeded ? await this.holdToken(proposal) : null;
    if (tokenNeeded && holdToken === null) return null;
    const snapshot = this.state.snapshot;
    const index = snapshot?.proposals.findIndex((item) => item.id === proposalId) ?? -1;
    // The proposal may have changed or gone while the token was on its way.
    const current = snapshot?.proposals[index];
    if (!snapshot || !current || current.payloadHash !== proposal.payloadHash) {
      if (hold) this.showToast('Öneri değişti · yeniden bak', { tone: 'danger', icon: 'alert' });
      return null;
    }
    const pending: PendingApproval = { ticketId: null, proposal: current, index, applyAt: null };
    this.set({
      snapshot: { ...snapshot, proposals: snapshot.proposals.filter((item) => item.id !== proposalId) },
      pending: [...this.state.pending, pending],
    });
    let ticket: ApprovalTicket;
    try {
      ticket = await this.api.approve(proposalId, {
        payloadHash: current.payloadHash,
        editedText,
        holdToken,
        copyConfirmed: options.copied === true,
      });
    } catch (error) {
      this.restorePending(pending);
      this.decisionFailed(error, 'Onay kaydedilemedi', 'bağlantıyı kontrol et');
      return null;
    }
    // The kill switch took the item back while the request was in flight; the server
    // undid the held action and requeued it, so there is no undo window to show.
    if (!this.state.pending.includes(pending)) return null;
    const executor = current.executor;
    if (ticket.applyAt === null) {
      // Nothing for the executor to apply: Sercan finishes it by hand (2.10 row 14).
      // v0.1.223: a copied new listing draft is "done" (Yaptım), not "sent" (Gönderdim).
      const copy = executor.kind === 'manual_copy' && current.kind === 'customer_reply';
      this.set({
        pending: this.state.pending.filter((item) => item !== pending),
        awaitingSend: [
          ...this.state.awaitingSend.filter((item) => item.proposalId !== proposalId),
          { proposalId: ticket.ticketId, title: current.title, copy },
        ],
      });
      if (executor.kind === 'manual_copy' && executor.copyOnApprove === false) {
        // v0.1.114: Twin makes the download link; the linked text is copied from Gönderilecekler.
        this.showToast("Onaylandı · indirme linki hazırlanıyor, linkli metin Gönderilecekler'de", { icon: 'info' });
      } else if (executor.kind === 'manual_copy') {
        this.showToast('Kopyalandı', { link: { label: `${executor.openLabel} ↗`, href: executor.openUrl } });
      } else {
        this.showToast("Onaylandı · elle yapılacak, Yapılanlar'da bekliyor", { icon: 'info' });
      }
      return ticket;
    }
    const held: PendingApproval = { ...pending, ticketId: ticket.ticketId, applyAt: ticket.applyAt };
    this.set({ pending: this.state.pending.map((item) => (item === pending ? held : item)) });
    const windowMs = ticket.applyAt - ticket.approvedAt;
    const durationMs = Number.isFinite(windowMs)
      ? Math.max(0, windowMs)
      : (this.state.snapshot?.executor.undoSeconds ?? 10) * 1000;
    const message = executor.kind === 'manual_copy' ? executor.consequence : executor.toast;
    const toastId = this.showToast(message, {
      countdown: { startedAt: this.localNow(), durationMs },
      action: { label: 'Geri al', run: () => void this.undo(ticket.ticketId), keepOpen: true },
    });
    this.undoToasts.set(ticket.ticketId, toastId);
    return ticket;
  }

  private restorePending(pending: PendingApproval): void {
    const snapshot = this.state.snapshot;
    const remaining = this.state.pending.filter((item) => item.proposal.id !== pending.proposal.id);
    if (!snapshot) {
      this.set({ pending: remaining });
      return;
    }
    const proposals = snapshot.proposals.filter((item) => item.id !== pending.proposal.id);
    proposals.splice(Math.min(pending.index, proposals.length), 0, pending.proposal);
    this.set({ snapshot: { ...snapshot, proposals }, pending: remaining });
  }

  /**
   * Geri al: cancels the held action at the executor. The proposal comes back to the queue
   * under the new id the server gave it (proposal.added, then a snapshot reload), never as
   * the old object: the undone approval is not pending any more.
   */
  async undo(ticketId: string): Promise<boolean> {
    const pending = this.state.pending.find((item) => item.ticketId === ticketId);
    if (!pending) return false;
    let requeuedProposalId: string | null;
    try {
      ({ requeuedProposalId } = await this.api.undo(ticketId));
    } catch (error) {
      if (error instanceof AlreadyAppliedError) {
        this.closeUndoToast(ticketId);
        this.showToast("Geri alınamadı: uygulandı · Yapılanlar'dan eski hâline dön", { tone: 'danger', icon: 'alert' });
      } else {
        // The undo toast stays open while the window lasts, so Geri al can be tried again.
        this.showToast(`Geri alma gitmedi · ${decisionErrorText(error, 'tekrar dene')}`, {
          tone: 'danger',
          icon: 'alert',
        });
      }
      return false;
    }
    this.closeUndoToast(ticketId);
    this.set({ pending: this.state.pending.filter((item) => item !== pending) });
    this.showToast(
      requeuedProposalId === null
        ? 'Geri alındı · aynı konuda daha yeni bir öneri kuyrukta'
        : 'Geri alındı · kuyruğa döndü',
      { icon: 'info' },
    );
    void this.refresh();
    return true;
  }

  /** Adds or replaces a Yapılanlar row. */
  private upsertDone(item: DoneItem): void {
    this.patchSnapshot((s) => ({ ...s, done: [item, ...s.done.filter((row) => row.id !== item.id)] }));
  }

  private takePending(ticketId: string): PendingApproval | undefined {
    const pending = this.state.pending.find((item) => item.ticketId === ticketId);
    this.closeUndoToast(ticketId);
    if (pending) this.set({ pending: this.state.pending.filter((item) => item !== pending) });
    return pending;
  }

  private onApplied(ticketId: string, at: Instant): void {
    const pending = this.takePending(ticketId);
    if (!pending) return;
    if (!this.state.snapshot?.done.some((item) => item.id === ticketId)) {
      // The done event with the server's row follows; until then the panel shows its own.
      this.upsertDone({
        id: ticketId,
        at,
        title: pending.proposal.title,
        result: 'Yürütücü',
        actor: 'executor',
        revertible: pending.proposal.actionType === 'listing_text_update',
        status: 'applied',
      });
    }
    this.showToast(`Uygulandı ${formatClock(at)} · Yapılanlar`, { durationMs: APPLIED_TOAST_MS });
  }

  /** Dry run (K8): the executor recorded its plan and changed nothing outside. */
  private onDryRun(ticketId: string): void {
    const pending = this.takePending(ticketId);
    if (!pending) return;
    this.showToast('Deneme modu: kaydedildi, uygulanmadı', { icon: 'info', durationMs: APPLIED_TOAST_MS });
  }

  /**
   * The executor could not apply an approved action. It is not pending any more (a new
   * approval would get 409 not_pending), so it does not go back to the queue: the done
   * event shows the failed row in Yapılanlar.
   */
  private onApplyFailed(ticketId: string, reason: string): void {
    this.takePending(ticketId);
    this.showToast(`Yürütücü uygulayamadı · ${reason}`, { tone: 'danger', icon: 'alert' });
  }

  private onDone(item: DoneItem): void {
    const pending = this.state.pending.find((entry) => entry.ticketId === item.id);
    if (pending && this.stillInWindow(pending, item, this.clock.now())) return;
    if (pending) this.takePending(item.id);
    else this.closeUndoToast(item.id);
    if (item.status !== 'awaiting_send') {
      const awaiting = this.state.awaitingSend.filter((entry) => entry.proposalId !== item.id);
      if (awaiting.length !== this.state.awaitingSend.length) this.set({ awaitingSend: awaiting });
    }
    this.upsertDone(item);
  }

  async reject(proposalId: string, reason: RejectReasonCode, note: string): Promise<boolean> {
    const snapshot = this.state.snapshot;
    const proposal = snapshot?.proposals.find((item) => item.id === proposalId);
    if (!snapshot || !proposal) return false;
    const index = snapshot.proposals.indexOf(proposal);
    this.holds.delete(proposalId);
    this.set({ snapshot: { ...snapshot, proposals: snapshot.proposals.filter((item) => item.id !== proposalId) } });
    try {
      await this.api.reject(proposalId, reason, note, proposal.payloadHash);
    } catch (error) {
      this.restorePending({ ticketId: null, proposal, index, applyAt: null });
      this.decisionFailed(error, 'Ret kaydedilemedi', 'tekrar dene');
      return false;
    }
    this.set({
      rejected: mergeRejected(
        [{ proposalId, title: proposal.title, reasonCode: reason, at: this.clock.now() }],
        this.state.rejected,
      ),
    });
    this.showToast('Reddedildi · gerekçe ajana iletildi', { icon: 'info' });
    return true;
  }

  /**
   * Runs an optimistic change against the API. When the call fails the local change is
   * rolled back and Sercan is told, so the panel never shows as done what the server did
   * not record.
   */
  private async attempt(run: () => Promise<void>, rollback: () => void, failure: string): Promise<boolean> {
    try {
      await run();
      return true;
    } catch (error) {
      rollback();
      this.showToast(failure, { tone: 'danger', icon: 'alert' });
      // No answer or a 5xx: the write may have landed, so read the state again.
      if (needsSnapshotRefresh(error)) void this.refresh();
      return false;
    }
  }

  async markSent(proposalId: string): Promise<void> {
    const item = this.state.awaitingSend.find((entry) => entry.proposalId === proposalId);
    this.set({
      awaitingSend: this.state.awaitingSend.filter((entry) => entry.proposalId !== proposalId),
      askSent: false,
    });
    const ok = await this.attempt(
      () => this.api.markSent(proposalId),
      () => {
        if (item) this.set({ awaitingSend: [...this.state.awaitingSend, item] });
      },
      'İşaretlenemedi · bağlantıyı kontrol et',
    );
    if (ok) {
      this.showToast('İşaretlendi · Yapılanlar', { icon: 'check' });
      // v0.1.114: a delivery card in Gönderilecekler turns to Gönderildi from the snapshot.
      void this.refresh();
    }
  }

  /** Gönderilecekler (v0.1.54): queues the Gmail send of an approved e-mail reply; the worker sends it. */
  async sendReply(approvalId: string): Promise<void> {
    const ok = await this.attempt(
      () => this.api.sendReply(approvalId),
      () => {},
      'Gönderilemedi · bağlantıyı kontrol et',
    );
    if (ok) {
      this.showToast("Sıraya alındı · Gmail'den gönderiliyor", { icon: 'check' });
      void this.refresh();
    }
  }

  dismissSentPrompt(): void {
    this.set({ askSent: false });
  }

  /** Called when the app becomes visible again: asks about copied replies. */
  onReturn(): void {
    if (this.state.awaitingSend.length > 0) this.set({ askSent: true });
  }

  /**
   * Eski hâline dön: a new listing_revert approval (T3) that the executor applies after its
   * own undo window, so it needs live data and a running executor, and it can be undone too.
   */
  async revert(doneId: string): Promise<void> {
    const blocker = this.approveBlocker();
    if (blocker === 'offline' || blocker === 'stopped' || blocker === 'paused') {
      this.showToast(
        blocker === 'offline' ? 'Bağlantı bekleniyor · eski hâline dönmek sonra' : 'Yürütücü durdu · önce Devam et',
        { tone: 'danger', icon: 'alert' },
      );
      return;
    }
    const before = this.state.snapshot?.done ?? [];
    this.patchSnapshot((s) => ({ ...s, done: s.done.filter((item) => item.id !== doneId) }));
    let ticket: ApprovalTicket;
    try {
      ticket = await this.api.revert(doneId);
    } catch (error) {
      this.patchSnapshot((s) => ({
        ...s,
        done: [...before.filter((row) => !s.done.some((d) => d.id === row.id)), ...s.done],
      }));
      this.decisionFailed(error, 'Eski hâline dönülemedi', 'tekrar dene');
      return;
    }
    const durationMs =
      ticket.applyAt === null || !Number.isFinite(ticket.applyAt - ticket.approvedAt)
        ? 0
        : Math.max(0, ticket.applyAt - ticket.approvedAt);
    const toastId = this.showToast('Eski hâline döndürülüyor · yürütücü uygular', {
      icon: 'info',
      ...(durationMs > 0
        ? {
            countdown: { startedAt: this.localNow(), durationMs },
            action: { label: 'Geri al', run: () => void this.undoRevert(ticket.ticketId, before), keepOpen: true },
          }
        : {}),
    });
    if (durationMs > 0) this.undoToasts.set(ticket.ticketId, toastId);
  }

  /** Geri al on a revert: the new listing_revert approval is undone; nothing is requeued. */
  private async undoRevert(ticketId: string, before: DoneItem[]): Promise<void> {
    try {
      await this.api.undo(ticketId);
    } catch (error) {
      if (error instanceof AlreadyAppliedError) {
        this.closeUndoToast(ticketId);
        this.showToast('Geri alınamadı: eski hâline dönüş uygulandı', { tone: 'danger', icon: 'alert' });
      } else {
        this.showToast(`Geri alma gitmedi · ${decisionErrorText(error, 'tekrar dene')}`, {
          tone: 'danger',
          icon: 'alert',
        });
      }
      return;
    }
    this.closeUndoToast(ticketId);
    this.patchSnapshot((s) => ({
      ...s,
      done: [...before.filter((row) => !s.done.some((d) => d.id === row.id)), ...s.done],
    }));
    this.showToast('Geri alındı · değişiklik yerinde kaldı', { icon: 'info' });
    void this.refresh();
  }

  // ---- requests ------------------------------------------------------------

  /** Applies a request status locally and returns the rollback. */
  private setRequest(id: string, status: RequestStatus, statusNote: string | null): () => void {
    const previous = this.state.snapshot?.requests.find((request) => request.id === id);
    this.patchSnapshot((s) => ({
      ...s,
      requests: s.requests.map((request) => (request.id === id ? { ...request, status, statusNote } : request)),
    }));
    return () => {
      if (!previous) return;
      this.patchSnapshot((s) => ({
        ...s,
        requests: s.requests.map((request) =>
          request.id === id ? { ...request, status: previous.status, statusNote: previous.statusNote } : request,
        ),
      }));
    };
  }

  async answerRequest(id: string, text: string, addToKb: boolean): Promise<void> {
    const rollback = this.setRequest(id, 'answered', null);
    const ok = await this.attempt(
      () => this.api.answerRequest(id, { text, addToKb }),
      rollback,
      'Cevap kaydedilemedi · tekrar dene',
    );
    if (ok)
      this.showToast(addToKb ? 'Cevap kaydedildi · bilgi bankasına eklendi' : 'Cevap kaydedildi', { icon: 'check' });
  }

  /** Kabul et with a target day (IsoDate, a Europe/Istanbul day; the API stores 23:59 of it). */
  async acceptRequest(id: string, targetDate: string, note: string): Promise<void> {
    const rollback = this.setRequest(id, 'accepted', acceptedRequestNote(targetDate));
    const ok = await this.attempt(
      () => this.api.acceptRequest(id, { targetDate, note }),
      rollback,
      'Kabul kaydedilemedi · tekrar dene',
    );
    if (ok) this.showToast(`Kabul edildi · hedef ${formatIsoDay(targetDate)}`, { icon: 'check' });
  }

  /** Ertele until a day (IsoDate; the API stores 09:00 Europe/Istanbul of it). */
  async deferRequest(id: string, until: string): Promise<void> {
    const rollback = this.setRequest(id, 'deferred', deferredRequestNote(until));
    const ok = await this.attempt(
      () => this.api.deferRequest(id, until),
      rollback,
      'Erteleme kaydedilemedi · tekrar dene',
    );
    if (ok) this.showToast(`Ertelendi · ${formatIsoDay(until)}`, { icon: 'info' });
  }

  async rejectRequest(id: string, reason: RequestRejectCode, note: string): Promise<void> {
    const rollback = this.setRequest(id, 'rejected', REQUEST_REJECT_REASON_LABELS[reason]);
    const ok = await this.attempt(
      () => this.api.rejectRequest(id, reason, note),
      rollback,
      'Ret kaydedilemedi · tekrar dene',
    );
    if (ok) this.showToast('Reddedildi · gerekçe ekibe iletildi', { icon: 'info' });
  }

  // ---- team ----------------------------------------------------------------

  /** Applies a pause or resume locally and returns the rollback. */
  private setAgentState(id: AgentId, paused: boolean): () => void {
    const previous = this.state.snapshot?.agents.find((agent) => agent.id === id);
    this.patchSnapshot((s) => ({
      ...s,
      agents: s.agents.map((agent) => {
        if (agent.id !== id) return agent;
        const resumed = agent.progress ? 'working' : agent.waitingFor ? 'waiting' : 'idle';
        return { ...agent, state: paused ? 'paused' : resumed, stateSince: this.clock.now() };
      }),
    }));
    return () => {
      if (!previous) return;
      this.patchSnapshot((s) => ({
        ...s,
        agents: s.agents.map((agent) =>
          agent.id === id ? { ...agent, state: previous.state, stateSince: previous.stateSince } : agent,
        ),
      }));
    };
  }

  async pauseAgent(id: AgentId): Promise<void> {
    const agent = this.state.snapshot?.agents.find((item) => item.id === id);
    if (!agent) return;
    const rollback = this.setAgentState(id, true);
    const ok = await this.attempt(
      () => this.api.pauseAgent(id),
      rollback,
      `${agent.name} duraklatılamadı · bağlantıyı kontrol et`,
    );
    if (!ok) return;
    this.showToast(`${agent.name} duraklatılıyor · mevcut adımı bitirecek`, {
      icon: 'pause',
      countdown: { startedAt: this.localNow(), durationMs: 10_000 },
      action: { label: 'Geri al', run: () => void this.resumeAgent(id) },
    });
  }

  async resumeAgent(id: AgentId): Promise<void> {
    const agent = this.state.snapshot?.agents.find((item) => item.id === id);
    const rollback = this.setAgentState(id, false);
    await this.attempt(
      () => this.api.resumeAgent(id),
      rollback,
      `${agent?.name ?? 'Ajan'} devam ettirilemedi · tekrar dene`,
    );
  }

  /** Duraklat: soft stop for everyone, the current steps finish, tap plus undo toast. */
  async pauseTeam(): Promise<void> {
    if (this.state.team.mode !== 'running') return;
    const previous = this.state.team;
    this.set({ team: { mode: 'paused', since: this.clock.now() } });
    const ok = await this.attempt(
      () => this.api.pauseTeam(),
      () => this.set({ team: previous }),
      'Duraklatma isteği gitmedi · bağlantıyı kontrol et',
    );
    if (!ok) return;
    this.showToast('Ekip duraklatılıyor · mevcut adımlar bitecek', {
      icon: 'pause',
      countdown: { startedAt: this.localNow(), durationMs: 10_000 },
      action: { label: 'Geri al', run: () => void this.resumeTeam() },
    });
  }

  /**
   * Durdur: hard stop after the 2 s hold. The server undoes every approval inside its undo
   * window and requeues it under a new id; the panel drops its held items and reloads.
   */
  async emergencyStop(): Promise<void> {
    let result: Awaited<ReturnType<TwinApi['emergencyStop']>>;
    try {
      result = await this.api.emergencyStop();
    } catch {
      this.showToast('Durdurma isteği gitmedi · bağlantı yok · tekrar dene', { tone: 'danger', icon: 'alert' });
      return;
    }
    for (const pending of this.state.pending) {
      if (pending.ticketId) this.closeUndoToast(pending.ticketId);
    }
    this.holds.clear();
    this.set({ pending: [], team: { mode: 'stopped', since: result.stoppedAt } });
    if (result.requeued.length > 0) {
      this.showToast(`${result.requeued.length} onay geri alındı · kuyruğa döndü`, { icon: 'info' });
    }
    void this.refresh();
  }

  /**
   * Durdurmadan sonra devam: the hold on Devam et started. Going back to running after a
   * stop loosens the system, so like a T4 approval it needs the server's hold token, asked now.
   */
  beginResumeHold(): void {
    if (this.state.team.mode !== 'stopped') return;
    const ticket = this.api.startResumeHold();
    ticket.catch(() => {});
    this.resumeHold = ticket;
  }

  cancelResumeHold(): void {
    this.resumeHold = null;
  }

  /**
   * Devam et: the team runs again only after the server confirms it. From a pause it is one
   * tap; from a stop it needs the completed hold, live data and the hold token (sent no
   * earlier than its notBefore).
   */
  async resumeTeam(options: { confirmedByHold?: boolean } = {}): Promise<void> {
    let holdToken: string | null = null;
    if (this.state.team.mode === 'stopped') {
      const asked = this.resumeHold;
      this.resumeHold = null;
      if (options.confirmedByHold !== true) {
        this.showToast('Durdurmadan sonra devam için 1,5 sn basılı tut', { icon: 'info' });
        return;
      }
      if (!this.liveView()) {
        this.showToast('Bağlantı bekleniyor · devam canlı veriyle yapılır', { tone: 'danger', icon: 'alert' });
        return;
      }
      let ticket: HoldTicket;
      try {
        ticket = await (asked ?? this.api.startResumeHold());
      } catch (error) {
        this.showToast(`Devam isteği gitmedi · ${decisionErrorText(error, 'bağlantıyı kontrol et')}`, {
          tone: 'danger',
          icon: 'alert',
        });
        return;
      }
      const wait = ticket.sendNotBeforeLocal - this.monoNow();
      if (wait > 0) await this.sleep(Math.ceil(wait));
      // The stream may have dropped while waiting for notBefore.
      if (!this.liveView()) {
        this.showToast('Bağlantı bekleniyor · devam canlı veriyle yapılır', { tone: 'danger', icon: 'alert' });
        return;
      }
      holdToken = ticket.holdToken;
    }
    let pendingExecutions = 0;
    try {
      ({ pendingExecutions } = await this.api.resumeTeam(holdToken));
    } catch (error) {
      this.showToast(`Devam isteği gitmedi · ${decisionErrorText(error, 'bağlantıyı kontrol et')}`, {
        tone: 'danger',
        icon: 'alert',
      });
      return;
    }
    this.set({ team: { mode: 'running', since: null } });
    if (pendingExecutions > 0) {
      this.showToast(`Devam ediyor · ${pendingExecutions} onaylı iş şimdi uygulanacak`, { icon: 'info' });
    }
  }

  // ---- settings ------------------------------------------------------------

  /** A limit is only loosened on live data: the stream is up and the last heartbeat is fresh. */
  canLoosen(): boolean {
    return this.liveView();
  }

  async loadSettings(): Promise<void> {
    try {
      const settings = await this.api.getSettings();
      setCurrencyDisplay(settings.preferences.currency);
      this.set({ settings, settingsError: null });
    } catch (error) {
      this.set({ settingsError: error instanceof Error ? error.message : String(error) });
    }
    await this.loadConnections();
  }

  // ---- connection values (0.4.1) ---------------------------------------------

  async loadConnections(): Promise<void> {
    try {
      const connections = await this.api.getConnections();
      this.set({ connections, connectionsError: null });
    } catch (error) {
      this.set({ connectionsError: error instanceof Error ? error.message : String(error) });
    }
  }

  private setConnectionBusy(slot: ConnectionSlot, busy: boolean): void {
    const current = this.state.connectionBusy.filter((item) => item !== slot);
    this.set({ connectionBusy: busy ? [...current, slot] : current });
  }

  private connectionFailureText(error: unknown, fallback: string): string {
    if (error instanceof TwinApiError) return error.message.trim() === '' ? fallback : error.message;
    if (error instanceof TransportError) return 'İstek gitmedi · bağlantıyı kontrol et';
    return fallback;
  }

  /**
   * Kaydet: sends the value once and forgets it. The server checks the shape and keeps the
   * value sealed; the panel only ever gets last4 back. A save replaces the previous value.
   */
  async saveConnection(slot: ConnectionSlot, value: string): Promise<boolean> {
    const trimmed = value.trim();
    if (trimmed === '') return false;
    if (!connectionValueAccepted(slot, trimmed)) {
      this.showToast(ERROR_MESSAGES_TR.connection_value_invalid, { tone: 'danger', icon: 'alert' });
      return false;
    }
    if (this.state.connectionBusy.includes(slot)) return false;
    this.setConnectionBusy(slot, true);
    try {
      const saved = await this.api.setConnection(slot, trimmed);
      this.showToast(`Kaydedildi ...${saved.last4} · değer bir daha gösterilmez`, { icon: 'check' });
      await this.loadConnections();
      await this.loadSettings();
      return true;
    } catch (error) {
      this.showToast(`Kaydedilmedi · ${this.connectionFailureText(error, 'tekrar dene')}`, {
        tone: 'danger',
        icon: 'alert',
      });
      return false;
    } finally {
      this.setConnectionBusy(slot, false);
    }
  }

  /** Sil: clears the stored value on the server; the service goes back to waiting. */
  async clearConnection(slot: ConnectionSlot): Promise<boolean> {
    if (this.state.connectionBusy.includes(slot)) return false;
    this.setConnectionBusy(slot, true);
    try {
      await this.api.clearConnection(slot);
      this.showToast('Silindi · servis bağlantı bekliyor', { icon: 'info' });
      await this.loadConnections();
      await this.loadSettings();
      return true;
    } catch (error) {
      this.showToast(`Silinmedi · ${this.connectionFailureText(error, 'tekrar dene')}`, {
        tone: 'danger',
        icon: 'alert',
      });
      return false;
    } finally {
      this.setConnectionBusy(slot, false);
    }
  }

  /**
   * Bağlantıyı dene: the server runs the test (the worker, for Telegram and Genesis); the
   * panel shows "deneniyor" and polls the connections read until the result is in, at most
   * CONNECTION_TEST_POLL_LIMIT_MS.
   */
  async testConnection(slot: ConnectionSlot): Promise<void> {
    if (this.state.connectionBusy.includes(slot)) return;
    this.setConnectionBusy(slot, true);
    try {
      const started = await this.api.testConnection(slot);
      this.patchConnectionTest(slot, started.test);
      const deadline = this.monoNow() + CONNECTION_TEST_POLL_LIMIT_MS;
      let test = started.test;
      while ((test.status === 'queued' || test.status === 'running') && this.monoNow() < deadline) {
        await this.sleep(CONNECTION_TEST_POLL_MS);
        const connections = await this.api.getConnections();
        this.set({ connections, connectionsError: null });
        test = connections.slots.find((item) => item.slot === slot)?.test ?? test;
      }
      if (test.status === 'ok')
        this.showToast(`Bağlantı çalışıyor${test.detail ? ` · ${test.detail}` : ''}`, { icon: 'check' });
      else if (test.status === 'failed') {
        this.showToast(`Bağlantı denemesi başarısız${test.detail ? ` · ${test.detail}` : ''}`, {
          tone: 'danger',
          icon: 'alert',
        });
      } else this.showToast('Deneme sürüyor · sonuç birazdan bu ekranda', { icon: 'info' });
    } catch (error) {
      this.showToast(`Denenemedi · ${this.connectionFailureText(error, 'tekrar dene')}`, {
        tone: 'danger',
        icon: 'alert',
      });
    } finally {
      this.setConnectionBusy(slot, false);
    }
  }

  private patchConnectionTest(slot: ConnectionSlot, test: ConnectionTest): void {
    const current = this.state.connections;
    if (!current) return;
    this.set({
      connections: {
        slots: current.slots.map((item) => (item.slot === slot ? { ...item, test } : item)),
      },
    });
  }

  /** The approval flow reads the discount cap and the undo window from the snapshot too. */
  private syncSnapshotPolicy(change: PolicyChange): void {
    if (change.kind !== 'number') return;
    if (change.id === 'discount_cap') {
      this.patchSnapshot((s) => ({ ...s, policy: { ...s.policy, discountCapPct: change.value } }));
    }
    if (change.id === 'approval_undo') {
      const seconds = change.value / 1000;
      this.patchSnapshot((s) => ({
        ...s,
        policy: { ...s.policy, undoSeconds: seconds },
        executor: { ...s.executor, undoSeconds: seconds },
      }));
    }
  }

  /**
   * Changes a policy value, flag, stage or agent switch. Tightening is one tap. Loosening
   * needs a written reason and the completed 1.5 s hold, and live data: a limit is never
   * loosened on values that may be out of date. Structural rules (bounds, prerequisites,
   * the team cap, locked stages) refuse the change either way. The server checks all of it
   * again; the audit entry it returns is what the change log shows. A tightening change
   * shows at once; a loosening one only after the server has accepted it.
   */
  async changePolicy(change: PolicyChange, options: PolicyChangeOptions = {}): Promise<AuditEntry | null> {
    const settings = this.state.settings;
    if (!settings) return null;
    const evaluation = evaluateChange(settings, change);
    if (evaluation.direction === 'none') return null;
    if (evaluation.violation) {
      this.showToast(evaluation.violation.message, { tone: 'danger', icon: 'alert' });
      return null;
    }
    const reason = reasonText(options.reason);
    const input = {
      change,
      reason: reason === '' ? null : reason,
      confirmedByHold: options.confirmedByHold === true,
    };
    const confirmation = confirmationViolation(evaluation, input);
    if (confirmation) {
      this.showToast(`${confirmation.message} · gevşetmek kayda gerekçesiyle yazılır`, { icon: 'info' });
      return null;
    }
    const loosening = evaluation.direction === 'loosen';
    if (loosening && !this.canLoosen()) {
      this.showToast('Bağlantı bekleniyor · gevşetme canlı veriyle yapılır', { tone: 'danger', icon: 'alert' });
      return null;
    }
    // Tightening shows at once and is rolled back if the server refuses it. Loosening is
    // never shown (or used by the approval flow) before the server has accepted it.
    const restore = loosening ? null : restoringChange(settings, change);
    if (!loosening) {
      this.set({ settings: applyChange(settings, change, this.clock.now()) });
      this.syncSnapshotPolicy(change);
    }
    let entry: AuditEntry;
    try {
      entry = await this.api.changePolicy(input);
    } catch (error) {
      const current = this.state.settings;
      if (restore && current) {
        this.set({ settings: applyChange(current, restore, this.clock.now()) });
        this.syncSnapshotPolicy(restore);
      }
      this.showToast(
        error instanceof PolicyRejectedError
          ? `Kaydedilmedi · ${error.message}`
          : 'Ayar kaydedilemedi · bağlantıyı kontrol et',
        { tone: 'danger', icon: 'alert' },
      );
      return null;
    }
    const current = this.state.settings;
    if (current) {
      const next = loosening ? applyChange(current, change, entry.at) : current;
      this.set({ settings: { ...next, audit: [entry, ...current.audit] } });
      if (loosening) this.syncSnapshotPolicy(change);
    }
    this.showToast(
      evaluation.direction === 'loosen'
        ? `Gevşetildi · ${evaluation.label} ${evaluation.to} · gerekçe kayda yazıldı`
        : `Sıkılaştırıldı · ${evaluation.label} ${evaluation.to} · kayda yazıldı`,
      { icon: 'check' },
    );
    return entry;
  }

  /**
   * Tüm yetkileri iptal et: only after the 2 s hold, and only as the server confirms it (the
   * executor deletes the token and revokes the grant at the provider).
   */
  async revokeConnection(id: string, options: { confirmedByHold?: boolean } = {}): Promise<boolean> {
    if (options.confirmedByHold !== true) {
      this.showToast('Yetkileri iptal etmek için 2 sn basılı tut', { icon: 'info' });
      return false;
    }
    const connection = this.state.settings?.connections.find((item) => item.id === id);
    if (!connection) return false;
    let entry: AuditEntry;
    try {
      entry = await this.api.revokeConnection(id, { confirmedByHold: true });
    } catch (error) {
      this.showToast(
        error instanceof PolicyRejectedError
          ? `İptal edilmedi · ${error.message}`
          : 'İptal isteği gitmedi · bağlantıyı kontrol et',
        { tone: 'danger', icon: 'alert' },
      );
      return false;
    }
    const at = entry.at;
    const current = this.state.settings;
    if (current) {
      this.set({
        settings: {
          ...current,
          connections: current.connections.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: 'not_connected',
                  statusNote: 'yetkiler iptal edildi',
                  phoneNote: 'yetkiler iptal edildi',
                  statusDetail: `Yetkiler ${formatClock(at)}'de iptal edildi; token silindi`,
                  action: 'connect',
                  lastCheckAt: at,
                }
              : item,
          ),
          audit: [entry, ...current.audit],
        },
      });
    }
    this.showToast(`${connection.name} yetkileri iptal edildi · token silindi`, { icon: 'check' });
    return true;
  }

  /** Tercihler: one tap, no reason. Rolled back when the server does not store it. */
  async updatePreferences(patch: PreferencesPatch): Promise<void> {
    const settings = this.state.settings;
    if (!settings) return;
    const before = settings.preferences;
    const next = {
      ...before,
      ...(patch.currency ? { currency: patch.currency } : {}),
      notifications: { ...before.notifications, ...patch.notifications },
    };
    this.set({ settings: { ...settings, preferences: next } });
    if (patch.currency) setCurrencyDisplay(patch.currency);
    await this.attempt(
      () => this.api.updatePreferences(patch),
      () => {
        const current = this.state.settings;
        if (current) this.set({ settings: { ...current, preferences: before } });
        setCurrencyDisplay(before.currency);
      },
      'Tercih kaydedilemedi · tekrar dene',
    );
  }

  /** Bağla opened the provider's consent page in a new tab; the panel never sees the grant. */
  noteConnectOpened(connection: ServiceConnection): void {
    const holder = connection.tokenHolder ? TOKEN_HOLDER_DATIVE[connection.tokenHolder] : 'sunucuya';
    this.showToast(`${connection.name} sayfası yeni sekmede açıldı · yetki doğrudan ${holder} gider`, {
      icon: 'info',
    });
  }
}
